import React from 'react';
import { GAME_MODE_CATALOG, type GameMode } from '@interactive-room';
import type { ActivityDraft } from '../../studio/activityStorage';
import { cn } from '../../lib/cn';
import { PollBuilder } from '../../studio/builders/PollBuilder';
import { ArenaBuilder } from '../../studio/builders/ArenaBuilder';
import { CoopBuilder } from '../../studio/builders/CoopBuilder';
import { SandboxBuilder } from '../../studio/builders/SandboxBuilder';
import { SequencerBuilder } from '../../studio/builders/SequencerBuilder';
import { PinpointBuilder } from '../../studio/builders/PinpointBuilder';
import { WallBuilder } from '../../studio/builders/WallBuilder';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './primitives/Dialog';

const BUILDERS: Record<
  GameMode,
  React.ComponentType<{ draftId?: string; embedded?: boolean; onSaved?: (d: ActivityDraft) => void }>
> = {
  poll: PollBuilder,
  arena: ArenaBuilder,
  coop: CoopBuilder,
  sandbox: SandboxBuilder,
  order: SequencerBuilder,
  point: PinpointBuilder,
  wall: WallBuilder,
};

const EXPANDED_FLASH_MODES: GameMode[] = ['order', 'arena', 'point', 'coop', 'sandbox'];

interface StudioFlashDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: GameMode;
  draftId?: string;
  onSaved: (draft: ActivityDraft) => void;
}

export function StudioFlashDialog({
  open,
  onOpenChange,
  mode,
  draftId,
  onSaved,
}: StudioFlashDialogProps) {
  const meta = GAME_MODE_CATALOG[mode];
  const Builder = BUILDERS[mode];
  const expanded = EXPANDED_FLASH_MODES.includes(mode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('sala-dialog--flash', expanded && 'sala-dialog--flash-expanded')}
        showClose
      >
        <DialogHeader>
          <DialogTitle>
            {meta.icon} Editar {meta.displayName}
          </DialogTitle>
          <DialogDescription>
            Edición rápida de emergencia — guarda y vuelve al catálogo para lanzar en la sala digital.
            Para crear material nuevo, usa Studio.
          </DialogDescription>
        </DialogHeader>
        <div className="sala-dialog__flash-body">
          {draftId ? (
            <Builder
              draftId={draftId}
              embedded
              onSaved={(draft) => {
                onSaved(draft);
                onOpenChange(false);
              }}
            />
          ) : (
            <p className="text-sm text-[var(--sala-ink-dim)]">
              Selecciona un borrador existente para editar desde la Consola.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
